import {
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Row,
  Tag,
  Upload,
  UploadFile
} from 'antd'
import { FormInstance } from 'antd/lib'

import { WriteOffDocumentResponse } from 'src/api/requests'
import {
  ClassifierInfiniteSelect,
  CustomItemRender,
  requiredRules,
  Text
} from 'src/components'
import { useStyles } from 'src/features/stock/pages/settlements/styles'
import { getSettlementStatus } from 'src/features/stock/utils'
import { onDownloadFile } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'

import { SettlementsConsumablesTable } from './SettlementsConsumablesTable'

type Props = {
  form: FormInstance
  item?: WriteOffDocumentResponse
}

type Document = UploadFile & { id?: string }

export const SettlementsConfirmedDetails = ({ form, item }: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles({ hasId: true })

  const medicineItems = Form.useWatch('medicine', form)

  const total = medicineItems?.reduce(
    (acc: number, item: { priceTotal: number }) =>
      acc + (item?.priceTotal ?? 0),
    0
  )

  const typedState = item?.state

  return (
    <Flex vertical gap={24}>
      <Card>
        <Flex vertical gap={24}>
          <Flex vertical gap={8}>
            <Flex justify="space-between" align="center">
              <Text weight={700} size="xxl">
                {t('stock_write_offs.write_off')}
              </Text>

              {!!typedState && (
                <Tag color={getSettlementStatus(t, typedState)?.color}>
                  {getSettlementStatus(t, typedState)?.text}
                </Tag>
              )}
            </Flex>
          </Flex>

          <Card className={styles.detailsCard}>
            <Flex vertical gap={24}>
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <Form.Item
                    name="documentDate"
                    label={t(`analysis.date`)}
                    rules={requiredRules}
                  >
                    <DatePicker
                      className="full-width"
                      format={dayMonthYearBackSlash}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="documentNumber"
                    label={t(`stock_write_offs.registration_number`)}
                    rules={requiredRules}
                  >
                    <Input
                      className="full-width"
                      placeholder={t(`stock_write_offs.registration_number`)}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="reasons"
                    label={t(`stock.return_reason`)}
                    rules={requiredRules}
                  >
                    <ClassifierInfiniteSelect mode="multiple" />
                  </Form.Item>
                </Col>
              </Row>
            </Flex>
          </Card>
        </Flex>
      </Card>

      <Card>
        {item?.initializedBy && (
          <Flex gap={8} align="center">
            <Text>{t('stock_write_offs.initialized_by')}:</Text>

            <Text>
              {item?.initializedBy?.firstName} {item?.initializedBy?.lastName} -{' '}
              {item?.legalEntity?.name}
            </Text>
          </Flex>
        )}

        {item?.processedBy && (
          <Flex gap={8} align="center">
            <Text>{t('stock_write_offs.processed_by')}:</Text>

            <Text>
              {item?.processedBy?.firstName} {item?.processedBy?.lastName}
            </Text>
          </Flex>
        )}

        {item?.restoredBy && (
          <Flex gap={8} align="center">
            <Text>{t('stock_write_offs.processed_by')}:</Text>

            <Text>
              {item?.restoredBy?.firstName} {item?.restoredBy?.lastName}
            </Text>
          </Flex>
        )}
      </Card>

      <Card>
        <Card title={t('stock.stocks')}>
          <Form.List name="medicine">
            {(fields, { add, remove }) => (
              <SettlementsConsumablesTable
                item={item}
                fields={fields}
                add={add}
                remove={remove}
                form={form}
              />
            )}
          </Form.List>

          <Flex justify="end" align="center" gap={8}>
            <Text weight={700}>{t('common.total')}: </Text>
            <Text weight={700}>{total ?? 0} MDL</Text>
          </Flex>
        </Card>
      </Card>

      <Card>
        <Card className={styles.detailsCard}>
          <Flex vertical gap={24}>
            <Card
              title={t('stock_applications.uploaded_documents')}
              className={styles.extraCardStyles}
            >
              <Upload
                itemRender={(_, file: Document) => (
                  <CustomItemRender
                    file={file}
                    onDownload={() => onDownloadFile(t, file?.id, file?.name)}
                  />
                )}
                defaultFileList={item?.mediaFiles?.map((file, index) => ({
                  ...file,
                  uid: String(index),
                  name: file?.name || `file-${index}`
                }))}
                disabled
              />
            </Card>

            {item?.comment && (
              <Card
                title={t('common.comment')}
                className={styles.extraCardStyles}
              >
                {item?.comment}
              </Card>
            )}
          </Flex>
        </Card>
      </Card>
    </Flex>
  )
}
